import { Action, action, createStore, persist, Thunk, thunk } from 'easy-peasy'
import gameViewModel, { GameViewModel, GameViewState } from '../features/gameView/GameViewModel'
import { PuzzleQuestModel, PuzzleQuestViewModel } from './../features/PuzzleQuest/PuzzleQuestModel'

import { WebSocketModel, WebsocketModel } from '../API/WebsocketModel'
import { RatingType } from '../components/matchmaking/ChallengeCard'
import ObserveViewModel, { ObserveViewConnectionModel } from '../features/ObserveView/ObserveViewModel'
import { PopupServiceModel } from '../features/PopupService/PopupServiceModel'
import ProfileViewModel, { ProfileModel } from '../features/Profile/ProfileViewModel'
import DailyPuzzleViewModel, { DailyPuzzleModel } from '../features/PuzzlesView/DailyPuzzleViewModel'
import PuzzlesViewModel, { PuzzlesModel } from '../features/PuzzlesView/PuzzlesViewModel'
import { RewardsServiceModel } from '../features/RewardService/RewardsModel'
import AnalysisViewModel, { GameAnalysisModel } from '../features/analysisView/AnalysisViewModel'
import { BasePGNHeaders } from '../features/analysisView/utils'
import matchMakingConnectionModel, {
    MatchMakingConnectionModel,
} from '../features/matchMakingDrawer/matchMakingConnector/MatchMakingConnectorModel'
import {
    GroupNames,
    LoggedInUserData,
    LoginState,
    Rules,
    UserData,
} from '../sharedComponents/src/globalHeader/GlobalHeader'
import GetCookie from '../sharedComponents/src/globalHeader/common/getCookie'
import {
    ensureLogin,
    getUserFollowList,
    tokenCookieKey,
    UserSearchResult,
} from '../sharedComponents/src/globalHeader/functions/ams-functions'
import { boardColors } from '../sharedComponents/src/globalHeader/theme/colors'
import { MMChallengeType, PGNHeader } from './types'

export type QueueTimeStart = {
    challengeId: string
    challengeType: MMChallengeType
    timeMode: string
    rated: boolean
    timestamp: number
    pairId?: string
}

export interface StoreModel {
    // stores
    analysisMode: GameAnalysisModel
    gameView: GameViewModel
    observeView: ObserveViewConnectionModel
    puzzlesView: PuzzlesModel
    dailyPuzzleView: DailyPuzzleModel
    puzzleQuestView: PuzzleQuestModel
    popupService: PopupServiceModel
    rewardsService: RewardsServiceModel
    matchMaker: MatchMakingConnectionModel
    ws: WebSocketModel
    // state
    // TODO: change this name since it is no longer a drawer
    drawerOpen: 'open' | 'closed' | 'loading'
    isDrawerMenuOpen: boolean
    serverConnectionData: boolean
    darkMode: boolean
    activeTab: number
    activeAnalysisTab: number
    userData: LoggedInUserData
    rules: Rules[]
    userGroups: GroupNames[]
    followList: Array<UserSearchResult>
    PGNHeader: PGNHeader
    unreadMessages: number
    queueTimeStarts: QueueTimeStart[]
    token: string | undefined
    snackbarMessage: string | undefined
    profileView: ProfileModel
    overlayTrigger: string | { name: string; data: any } | undefined

    // actions
    setServerConnectionData: Action<StoreModel, boolean>
    setToken: Action<StoreModel, string | undefined>
    setDrawerOpen: Action<StoreModel, 'open' | 'closed' | 'loading'>
    setIsDrawerMenuOpen: Action<StoreModel, boolean>
    setDarkMode: Action<StoreModel, boolean>
    setActiveTab: Action<StoreModel, number>
    setUserData: Action<StoreModel, LoggedInUserData>
    updateUserData: Action<StoreModel, UserData>
    setRules: Action<StoreModel, Rules[]>
    setUserGroups: Action<StoreModel, GroupNames[]>
    setActiveAnalysisTab: Action<StoreModel, number>
    setPGNHeader: Action<StoreModel, PGNHeader>
    setUnreadMessages: Action<StoreModel, number>
    setOverlayTrigger: Action<StoreModel, string | { name: string; data: any } | undefined>
    setUpdatedRating: Action<StoreModel, { type: RatingType; newRating: number | undefined }>
    setSnackbarMessage: Action<StoreModel, string | undefined>
    addQueueTimeStart: Action<StoreModel, QueueTimeStart>
    removeQueueTimeStart: Action<StoreModel, string>
    updateQueueTimeStart: Action<StoreModel, { challengeId: string; pairId: string }>
    clearQueueTimeStarts: Action<StoreModel>
    setUserFollowList: Action<StoreModel, Array<UserSearchResult>>

    // thunks
    initUser: Thunk<StoreModel>
    updateUserFollowList: Thunk<StoreModel>
}

export const store = createStore<StoreModel>({
    // TODO: Remove single state entities from global store
    drawerOpen: 'closed',
    isDrawerMenuOpen: false,
    serverConnectionData: false,
    darkMode: true,
    activeTab: 0,
    activeAnalysisTab: 0,
    queueTimeStarts: [],
    token: undefined,
    unreadMessages: 0,
    matchMaker: matchMakingConnectionModel,
    rules: [],
    userGroups: [],
    followList: [],
    PGNHeader: BasePGNHeaders,

    analysisMode: AnalysisViewModel,
    gameView: gameViewModel,
    observeView: ObserveViewModel,
    puzzlesView: PuzzlesViewModel,
    dailyPuzzleView: DailyPuzzleViewModel,
    puzzleQuestView: PuzzleQuestViewModel,
    profileView: persist(ProfileViewModel, {
        allow: ['profileMenu', 'recordAmount', 'filter', 'gamesPerPage', 'sort', 'activePage'],
        storage: 'sessionStorage',
    }),
    popupService: PopupServiceModel,
    rewardsService: RewardsServiceModel,
    ws: WebsocketModel,
    userData: { state: LoginState.INITIALIZING, userData: null, token: '' },
    overlayTrigger: undefined,
    snackbarMessage: undefined,

    setDarkMode: action((state, on) => {
        state.darkMode = on
    }),
    setDrawerOpen: action((state, open) => {
        state.drawerOpen = open
    }),
    setIsDrawerMenuOpen: action((state, open) => {
        state.isDrawerMenuOpen = open
    }),
    setActiveTab: action((state, tab) => {
        state.activeTab = tab
    }),
    setActiveAnalysisTab: action((state, tab) => {
        state.activeAnalysisTab = tab
    }),
    setUserData: action((state, ud) => {
        state.userData = ud
    }),
    updateUserData: action((state, payload) => {
        state.userData.userData = payload
    }),
    setRules: action((state, groups) => {
        state.rules = groups
    }),
    setUserGroups: action((state, groups) => {
        state.userGroups = groups
    }),
    setPGNHeader: action((state, header) => {
        state.PGNHeader = header
    }),
    setUnreadMessages: action((state, num) => {
        state.unreadMessages = num
    }),
    setOverlayTrigger: action((state, str) => {
        state.overlayTrigger = str
    }),
    setUpdatedRating: action((state, payload) => {
        if (state.userData.userData) {
            state.userData.userData.ratings = {
                ...state.userData.userData.ratings,
                [payload.type]: {
                    ...state.userData.userData.ratings[payload.type],
                    rating: payload.newRating,
                },
            }
        }
    }),
    setToken: action((state, token) => {
        state.token = token
    }),
    setServerConnectionData: action((state, data) => {
        state.serverConnectionData = data
    }),
    setSnackbarMessage: action((state, message) => {
        state.snackbarMessage = message
    }),
    addQueueTimeStart: action((state, time) => {
        state.queueTimeStarts.push(time)
    }),
    removeQueueTimeStart: action((state, challengeId) => {
        const foundQueueTimeStart = state.queueTimeStarts.find((t) => t.challengeId === challengeId)
        if (!foundQueueTimeStart) return

        state.queueTimeStarts = state.queueTimeStarts.filter((t) => t.challengeId !== challengeId)
    }),
    updateQueueTimeStart: action((state, data) => {
        const foundQueueTimeStart = state.queueTimeStarts.find((t) => t.challengeId === data.challengeId)
        if (foundQueueTimeStart) {
            foundQueueTimeStart.pairId = data.pairId
        }
    }),
    clearQueueTimeStarts: action((state) => {
        state.queueTimeStarts = []
    }),
    initUser: thunk(async (actions) => {
        const data = await ensureLogin()
        if (!!data && !data.error) {
            const token = GetCookie(tokenCookieKey)
            actions.setUserData({
                state: LoginState.LOGGED_IN,
                userData: data,
                token: token,
            })
            actions.profileView.setCurrentAvatar(data.avatar_url || '')
            if (!!token) {
                actions.setToken(token)
            }
            if (data.groups) {
                const ruleNames = data.groups.flatMap((group) => group.rules.map((rule) => rule.name))
                actions.setRules(ruleNames)
                const userGroups = data.groups.map((group) => group.name)
                actions.setUserGroups(userGroups)
            }
            if (data.game_settings) {
                actions.gameView.setSettings({
                    ...data.game_settings,
                    boardStyle: boardColors[data.game_settings.boardStyle] ? data.game_settings.boardStyle : 'default',
                })
            }
            await actions.updateUserFollowList()
        } else {
            actions.gameView.shutDownGSConnection()
            actions.gameView.resetGame(GameViewState.PRE_GAME)
            actions.matchMaker.reset()
            actions.setUserData({ state: LoginState.NOT_LOGGED_IN, userData: null, token: '' })
            actions.setToken('')
        }
    }),
    updateUserFollowList: thunk(async (actions) => {
        const followsData = await getUserFollowList()
        actions.setUserFollowList(followsData)
    }),
    setUserFollowList: action((state, follows) => {
        state.followList = follows
    }),
})

export default store
